// React
import React, { FC, useContext, useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Context
import UserDetailsContext from 'Context/userDetailsContext';

// GraphQL
// This will need to be dealt with later, installing this
// broke the app
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';

// GoodData
import {
  getValidElements,
  loginSso,
} from 'Containers/GoodData/utilities/goodDataApiClient';

// Styles
import injectSheet from 'react-jss';

// Components
import { Loading } from 'Components/Loading';
import { Center } from 'Components/Helpers';

// Containers
import { GoodDataDashboardContainer } from 'Containers/GoodData/containers';

// Dashboards
import { ContentPerformanceDetails } from 'Containers/GoodData/dashboards';

// Utilities
import {
  getGoodDataApiDomain,
  modifyGoodDataFilterContext,
  useIwcDataWarning,
} from 'Containers/GoodData/utilities';
import { findDateFromDays } from 'Utils/findDateFromDays';
import { getRelativeDateFromToday } from 'Utils/getRelativeDateFromToday';
import { areEqual } from 'Utils/areEqual';
import { formatDatesForGoodData } from 'Containers/GoodData/utilities/formatDatesForGoodData';
import { formatDatesForCalendar } from 'Containers/GoodData/utilities/formatDatesForCalendar';
import { isUserOnOrPastSafariTwelve } from 'Containers/GoodData/utilities/isUserOnOrPastSafariTwelve';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import {
  setGoodDataEventValue,
  setUIDrillEventValue,
  setPlatformInfoEventValue,
  setDateRangeValue,
} from '../../actions/goodData.actions';
import styleSheet from './styles';
import goodDataGql from './gqlQuery';

interface Props {
  classes?: any;
  moduleAccess: any;
}

const ContentPerformanceOverview: FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const {
    goodDataEvent,
    platformInfoEvent,
    uiDrillEvent,
    dateRange,
    userBrowser,
  } = useSelector((selectors: any) => selectors.goodData, areEqual);
  const { selection, fromDate, toDate } = dateRange;

  const { classes, moduleAccess } = props;

  const {
    actions: { track },
  } = useContext(ProductAnalyticsContext);

  useEffect(() => {
    // Combined Analytics event tracking
    track('content_performance_page_viewed');
  }, []);

  // State that holds the GraphQL data.
  const [gdGQLData, setGDGQLData] = useState(undefined);

  // The following three states hold the sso data and each respective
  // dashboard data as passed back from the GoodDataDashboardContainer
  const [ssoConfiguration, setSsoConfiguration] = useState(undefined);
  const [vtDashboardConfiguration, setVTDashboardConfiguration] =
    useState(undefined);
  const [iwcDashboardConfiguration, setIWCDashboardConfiguration] =
    useState(undefined);

  // Abstract the loading to a variable
  const [isLoading, setIsLoading] = useState(true);

  // These two values govern the ContentPerformanceDetails dashboard.
  // when a drill event happens showModal is immediately set to true and
  // a function that eventually calls setDetailsFilterValue is called.
  // When a user exits the modal, showModal should be set to false and
  // detailsFilterValue should be undefined.
  const [detailsFilterValue, setDetailsFilterValue] = useState(undefined);
  const [showModal, setShowModal] = useState(false);

  // Check if institution has locations with IWC and VT dashboards otherwise restrict access //
  const [hasVT, setHasVt] = useState(undefined);
  const [hasIWC, setHasIWC] = useState(undefined);

  // These two constants are the dashboard names as the back-end knows them.
  const VT_OVERVIEW_DASHBOARD_NAME = 'analytics_cp_vt_overview_v2';
  const IWC_OVERVIEW_DASHBOARD_NAME = 'analytics_cp_iwc_overview_v2';

  // This constant is the label that is modified in modifyGoodDataFilterContext
  // calls for this dashboard.
  const DATE_FILTER_LABEL = 'report_date.date.day.us.mm_dd_yyyy';
  const INST_FILTER_LABEL =
    'label.cp_institution_daily_summary_with_network.institution_id';

  const userDetails = useContext(UserDetailsContext);

  // GraphQL User.GoodDataClaims Query
  const { loading, data: goodDataData } = useQuery(goodDataGql, {
    variables: {
      id: userDetails.userId,
      accountId: userDetails.accountId,
      instId: userDetails.instId,
    },
    skip: gdGQLData,
  });

  if (
    !loading &&
    gdGQLData &&
    gdGQLData.institution &&
    hasVT === undefined &&
    hasIWC === undefined
  ) {
    setHasVt(
      gdGQLData.institution.locations.filter(
        (location: any) => location.experience_type === 'vt'
      ).length > 0
    );
    setHasIWC(
      gdGQLData.institution.locations.filter(
        (location: any) => location.experience_type === 'iwc'
      ).length > 0
    );
  }

  // useEffect to update state when the goodDataData changes.  Should
  // really only happen once.
  useEffect(() => {
    if (goodDataData && !areEqual(goodDataData, gdGQLData)) {
      setGDGQLData(goodDataData);
    }
  }, [goodDataData]);

  // This function is called whenever a ui.drill event happens on either dashboard.
  // attribute comes from the GoodData ui.drill event received from our eventListeneGoodData
  // function.  It is found in event.gdc.data.executionContext.drillToTab.locators[0].attributeLocator2.
  // Project is the project ID and is found in the respective dashboard's configuration that is received
  // back from the GoodDataDashboardContainer whenever it is unwrapped.  Dashboard is found by checking
  // document.activeElement.id and comparing it to one of the dashboard names.  If it returns false it is
  // assumed that the other dashboard is active.
  const findGDElementValue = (
    attribute: any,
    project: any,
    dashboard: 'VT' | 'IWC'
  ) => {
    if (
      !ssoConfiguration ||
      !ssoConfiguration.loginUrl ||
      !ssoConfiguration.encryptedClaims ||
      !ssoConfiguration.ssoProvider
    ) {
      return;
    }

    const domain = getGoodDataApiDomain(ssoConfiguration.loginUrl);

    // The attributeDisplayForm can be gotten by hacking off the id after the last / of
    // attribute.uri
    const attributeDisplayForm = attribute.uri.split('/').slice(-1).pop();

    loginSso(
      domain,
      ssoConfiguration.encryptedClaims,
      ssoConfiguration.ssoProvider,
      attribute.uri
    )
      .then(() => {
        if (project && attributeDisplayForm) {
          return getValidElements(domain, project, attributeDisplayForm);
        }

        return undefined;
      })
      .then((response: any) => {
        if (!response) {
          return;
        }

        response.validElements.items.forEach((element: any) => {
          if (element.element.uri === attribute.element) {
            setDetailsFilterValue({
              dashboard,
              filterValue: element.element.title,
            });
          }
        });
      })
      .catch(() => {
        setShowModal(false);
        setDetailsFilterValue(undefined);
      });
  };

  // End loading early if we know there is nothing loading for
  // aesthetic purposes
  useEffect(() => {
    if (userBrowser && isUserOnOrPastSafariTwelve(userBrowser)) {
      setIsLoading(false);
      if (hasVT) {
        setHasIWC(false);
      }
    }
  }, [userBrowser]);

  // The first time both dashboards have loaded, change the filter context to 30 Days
  // (Product requirement).
  useEffect(() => {
    if (!showModal && platformInfoEvent && gdGQLData) {
      modifyGoodDataFilterContext(VT_OVERVIEW_DASHBOARD_NAME, [
        {
          label: DATE_FILTER_LABEL,
          from: fromDate,
          to: toDate,
          type: 'date',
        },
        {
          label: INST_FILTER_LABEL,
          value: gdGQLData.institution.instGuid,
        },
      ]);
      modifyGoodDataFilterContext(IWC_OVERVIEW_DASHBOARD_NAME, [
        {
          label: DATE_FILTER_LABEL,
          from: fromDate,
          to: toDate,
          type: 'date',
        },
        {
          label: INST_FILTER_LABEL,
          value: gdGQLData.institution.instGuid,
        },
      ]);

      setPlatformInfoEventValue(dispatch, false);
    }
  }, [platformInfoEvent, gdGQLData]);

  // Loading timeout to ensure we will show GoodData even if we have an event issue
  if (
    ssoConfiguration &&
    (vtDashboardConfiguration || iwcDashboardConfiguration)
  ) {
    setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
      }
    }, 5000);
  }

  // When a ui.drill event happens, immediately opens the modal, then
  // calls theb findGDElementValue function outlined above to convert the
  // execution context to a value that is usable by us.
  useEffect(() => {
    if (
      (document.activeElement.id === VT_OVERVIEW_DASHBOARD_NAME ||
        document.activeElement.id === IWC_OVERVIEW_DASHBOARD_NAME) &&
      uiDrillEvent &&
      goodDataEvent &&
      goodDataEvent.type === 'ui.drill'
    ) {
      setShowModal(true);
      findGDElementValue(
        goodDataEvent.event.gdc.data.executionContext.drillToTab.locators[0]
          .attributeLocator2,
        (document.activeElement.id === VT_OVERVIEW_DASHBOARD_NAME &&
          vtDashboardConfiguration &&
          vtDashboardConfiguration.dashboard &&
          vtDashboardConfiguration.dashboard.project) ||
          (iwcDashboardConfiguration &&
            iwcDashboardConfiguration.dashboard &&
            iwcDashboardConfiguration.dashboard.project),
        (document.activeElement.id === VT_OVERVIEW_DASHBOARD_NAME && 'VT') ||
          'IWC'
      );
      setGoodDataEventValue(dispatch, undefined);
      setUIDrillEventValue(dispatch, false);
    }
  }, [uiDrillEvent, goodDataEvent]);

  // Anytime a dropdown change happens the event filter context change
  // is propogated to both dashboards.
  const handleDropdownChange = (event: any) => {
    if (!areEqual(event.target, { label: 'Custom Date', value: 0 })) {
      setDateRangeValue(dispatch, {
        selection: event.target,
        fromDate: findDateFromDays(event.target.value),
        toDate: getRelativeDateFromToday(1),
      });
    } else {
      setDateRangeValue(dispatch, {
        selection: event.target,
        fromDate,
        toDate,
      });
    }
  };

  const handleCalendarChange = (
    selectedStartDate: string,
    selectedChangeEndDate: string
  ) => {
    setDateRangeValue(dispatch, {
      selection,
      fromDate: formatDatesForGoodData(selectedStartDate),
      toDate: formatDatesForGoodData(selectedChangeEndDate),
    });
  };

  useEffect(() => {
    if (gdGQLData) {
      modifyGoodDataFilterContext(VT_OVERVIEW_DASHBOARD_NAME, [
        {
          label: DATE_FILTER_LABEL,
          from: fromDate,
          to: toDate,
          type: 'date',
        },
        {
          label: INST_FILTER_LABEL,
          value: gdGQLData.institution.instGuid,
        },
      ]);
      modifyGoodDataFilterContext(IWC_OVERVIEW_DASHBOARD_NAME, [
        {
          label: DATE_FILTER_LABEL,
          from: fromDate,
          to: toDate,
          type: 'date',
        },
        {
          label: INST_FILTER_LABEL,
          value: gdGQLData.institution.instGuid,
        },
      ]);
    }
  }, [fromDate, toDate, gdGQLData]);

  // Show IWC data warning message if IWC data is not available in selected fromDate
  useIwcDataWarning(dispatch, fromDate, hasIWC);

  // When the modal closes, both states are set back to their default values.
  const handleClose = () => {
    setShowModal(false);
    setDetailsFilterValue(undefined);
  };

  return (
    <div
      className={
        userBrowser &&
        !isUserOnOrPastSafariTwelve(userBrowser) &&
        classes.backgroundContainer
      }
    >
      {isLoading && (
        <div className={classes.loader}>
          <Center>
            <Loading />
          </Center>
        </div>
      )}
      {hasVT && (
        <GoodDataDashboardContainer
          moduleAccess={moduleAccess}
          title="Virtual Tour Content Performance Overview Dashboard"
          name={VT_OVERVIEW_DASHBOARD_NAME}
          id={VT_OVERVIEW_DASHBOARD_NAME}
          gdData={gdGQLData}
          width="940px"
          height="740px"
          border="none"
          errorContainerStyles={{
            width: '1074px',
            height: '744px',
          }}
          ssoCallback={setSsoConfiguration}
          dashboardCallback={setVTDashboardConfiguration}
          handleDropdownChange={handleDropdownChange}
          startDate={formatDatesForCalendar(fromDate)}
          endDate={formatDatesForCalendar(toDate)}
          selectedOption={selection}
          handleCalendarChange={handleCalendarChange}
          useGoodDataDropdown={
            vtDashboardConfiguration && vtDashboardConfiguration.enabled
          }
          goodDataDropdownStyling={{
            height: '100px',
            width: '987px',
            backgroundColor: '#FFFFFF',
            position: 'relative',
            marginBottom: '-45px',
            zIndex: 8,
          }}
          marginLeft="17px"
        />
      )}
      {hasIWC && (
        <GoodDataDashboardContainer
          title="Interactive Web Content Content Performance Overview Dashboard"
          name={IWC_OVERVIEW_DASHBOARD_NAME}
          id={IWC_OVERVIEW_DASHBOARD_NAME}
          width="940px"
          height="720px"
          border="none"
          errorContainerStyles={{
            width: '1074px',
            height: '744px',
          }}
          gdData={gdGQLData}
          ssoCallback={setSsoConfiguration}
          dashboardCallback={setIWCDashboardConfiguration}
          handleDropdownChange={handleDropdownChange}
          startDate={formatDatesForCalendar(fromDate)}
          endDate={formatDatesForCalendar(toDate)}
          selectedOption={selection}
          handleCalendarChange={handleCalendarChange}
          useGoodDataDropdown={
            (vtDashboardConfiguration &&
              vtDashboardConfiguration.enabled &&
              iwcDashboardConfiguration &&
              iwcDashboardConfiguration.enabled &&
              false) ||
            (vtDashboardConfiguration &&
              !vtDashboardConfiguration.enabled &&
              iwcDashboardConfiguration &&
              iwcDashboardConfiguration.enabled)
          }
          goodDataDropdownStyling={{
            height: '45px',
            width: '987px',
            backgroundColor: '#FFFFFF',
            position: 'relative',
            marginBottom: '-45px',
            zIndex: 8,
          }}
          marginLeft="17px"
        />
      )}
      {showModal && (
        <ContentPerformanceDetails
          dashboard={detailsFilterValue && detailsFilterValue.dashboard}
          show={showModal}
          filterValue={detailsFilterValue && detailsFilterValue.filterValue}
          handleClose={handleClose}
        />
      )}
    </div>
  );
};

ContentPerformanceOverview.defaultProps = {
  classes: {},
};

export default withErrorBoundary(
  injectSheet(styleSheet)(ContentPerformanceOverview)
);
